<template>
  <div class="battery-sensor-module" :class="{ 'collapsed': isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse" :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-battery-charging" /> {{ langMap.dashboard_batteryAndSensors[lang] }}
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>
    
    <div class="devices-container" v-show="!isCollapsed">
      <!-- 无数据提示 -->
      <div v-if="deviceData.length === 0" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-battery-charging" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      
      <!-- 正常数据 -->
      <div 
        v-for="device in deviceData" 
        :key="device.deviceId"
        class="device-card"
        :class="{ 'full-width': isBatteryDevice(device.deviceName) }"
      >
        <div class="device-header">
          <Icon :type="getDeviceIcon(device.deviceName)" class="device-icon" />
          <span class="device-name-main">{{ device.name }}</span>
        </div>
        <!-- 电池类设备：网格布局（类似电机） -->
        <div v-if="isBatteryDevice(device.deviceName)" class="device-params params-grid">
          <div 
            v-for="param in device.params" 
            :key="param.paramName"
            class="param-item"
          >
            <div class="param-label">{{ param.label }}</div>
            <div class="param-value">{{ param.value }}<span class="unit">{{ param.unit }}</span></div>
          </div>
        </div>
        
        <!-- 其他设备（雷达等）：列表布局（类似相机） -->
        <div v-else class="device-params params-list">
          <div 
            v-for="param in device.params" 
            :key="param.paramName"
            class="param-row"
          >
            <span class="param-name">{{ param.label }}</span>
            <div class="param-value-group">
              <span class="param-value">{{ param.value }}</span>
              <span class="param-unit">{{ param.unit }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import collapsibleMixin from './collapsible-mixin';
import langMap from '@/utils/language';

export default {
  name: 'BatterySensorModule',
  mixins: [collapsibleMixin],
  props: {
    paramData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      langMap,
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    deviceData() {
      // 筛选电池和传感器相关数据（排除电机和相机）
      const devices = {};
      
      this.paramData.forEach(item => {
        const deviceName = item.deviceName || '';
        // 排除电机和相机
        if (!deviceName.includes('motor') && !deviceName.includes('camera')) {
          if (!devices[item.deviceId]) {
          devices[item.deviceId] = {
            deviceId: item.deviceId,
            deviceName: deviceName,
            name: this.lang === 'zh' ? item.deviceNameCn : item.deviceNameEn, // 保留原始数据
            params: []
          };
          }
          
          devices[item.deviceId].params.push({
            paramName: item.paramName,
            label: this.lang === 'zh' ? item.paramNameCn : item.paramNameEn, // 保留原始数据
            value: this.formatValue(item.data),
            unit: item.paramUnit
          });
        }
      });
      
      return Object.values(devices);
    }
  },
  methods: {
    getDeviceIcon(deviceName) {
      const iconMap = {
        'battery': 'ios-battery-charging',
        'lidar': 'ios-radio',
        'imu': 'ios-compass',
        'gps': 'ios-navigate'
      };
      return iconMap[deviceName] || 'ios-pulse';
    },
    isBatteryDevice(deviceName) {
      // 判断是否为电池类设备，只有电池使用网格布局
      if (!deviceName) return false;
      const name = deviceName.toLowerCase();
      return name === 'battery' || name.includes('battery');
    },
    formatValue(value) {
      // 处理数字或字符串类型的数值
      if (value === null || value === undefined || value === '') {
        return value;
      }
      
      // 尝试转换为数字
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      
      // 如果是有效的数字
      if (!isNaN(numValue)) {
        // 检查是否有小数部分且超过2位
        const decimalPart = (numValue.toString().split('.')[1] || '');
        if (decimalPart.length > 2) {
          return numValue.toFixed(2);
        }
        return numValue;
      }
      
      // 如果不是数字，返回原值
      return value;
    }
  }
}
</script>

<style lang="less" scoped>
.battery-sensor-module {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;
  
  &.collapsed {
    flex: 0 0 auto;
  }
  
  .sci-fi-title {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95));
      border-bottom-color: rgba(0, 240, 255, 0.8);
    }
    
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
        margin-left: auto;
      }
    }
    
    .title-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover .title-bg {
      opacity: 1;
    }
    
    .title-decoration {
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00f0ff, transparent);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 240, 255, 1) 50%, 
      transparent
    );
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.1),
      transparent
    );
    animation: dataflow 3s ease-in-out infinite;
  }
  
  .devices-container {
    flex: 1;
    padding: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    align-content: flex-start;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 240, 255, 0.3);
      border-radius: 2px;
    }
  }
  
  .device-card {
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.7), rgba(0, 20, 45, 0.9));
    border: 1px solid rgba(0, 240, 255, 0.4);
    border-radius: 8px;
    padding: 4px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(0, 240, 255, 0.2);
    flex: 0 0 calc(~"50% - 4px");
    
    // 电池设备占满整行
    &.full-width {
      flex: 0 0 100%;
    }
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: conic-gradient(
        transparent,
        rgba(0, 240, 255, 0.12),
        transparent 30%
      );
      animation: rotate 8s linear infinite;
      opacity: 0.6;
    }
    
    .device-header {
      display: flex;
      align-items: center;
      gap: 2px;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.3);
      position: relative;
      z-index: 1;
      
      .device-icon {
        font-size: 16px;
        color: #00f0ff;
        text-shadow: 0 0 12px rgba(0, 240, 255, 0.6);
      }
      
      .device-name-main {
        font-size: 12px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 12px rgba(0, 240, 255, 0.6);
        letter-spacing: 1px;
      }
      
      .device-name-sub {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.4);
        font-family: 'Consolas', monospace;
        margin-left: auto;
      }
    }
    
    .device-params {
      position: relative;
      z-index: 1;
      
      // 网格布局（电池类设备）
      &.params-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .param-item {
          background: linear-gradient(135deg, rgba(0, 240, 255, 0.12), rgba(0, 180, 255, 0.08));
          border: 1px solid rgba(0, 240, 255, 0.35);
          border-radius: 6px;
          padding: 4px;
          text-align: center;
          position: relative;
          overflow: hidden;
          flex: 0 0 ~"calc(25% - 6px)";
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2),
                      inset 0 1px 0 rgba(0, 240, 255, 0.15);
          
          .param-label {
            font-size: 10px;
            color: rgba(255, 255, 255, 0.65);
            margin-bottom: 4px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            letter-spacing: 0.5px;
            width: 100%;
          }
          
          .param-value {
            font-size: 16px;
            font-weight: 700;
            color: #fff;
            font-family: 'Consolas', 'Monaco', monospace;
            white-space: nowrap;
            text-shadow: 0 0 15px rgba(255, 255, 255, 0.6),
                         0 0 30px rgba(0, 240, 255, 0.5);
            transition: all 0.3s ease;
            position: relative;
            display: inline-block;
            line-height: 1;
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            
            .unit {
              font-size: 10px;
              color: rgba(0, 240, 255, 1);
              margin-left: 2px;
              font-weight: 600;
              text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
            }
          }
        }
      }
      
      // 列表布局（雷达等设备）
      &.params-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        
        .param-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 8px;
          background: linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 180, 255, 0.05));
          border: 1px solid rgba(0, 240, 255, 0.3);
          border-radius: 6px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2),
                      inset 0 1px 0 rgba(0, 240, 255, 0.1);
          
          .param-name {
            font-size: 9px;
            color: rgba(255, 255, 255, 0.6);
            white-space: nowrap;
            letter-spacing: 0.5px;
          }
          
          .param-value-group {
            display: flex;
            align-items: baseline;
            gap: 3px;
            
            .param-value {
              font-size: 14px;
              font-weight: 700;
              color: #fff;
              font-family: 'Consolas', 'Monaco', monospace;
              white-space: nowrap;
              text-shadow: 0 0 15px rgba(255, 255, 255, 0.6),
                           0 0 30px rgba(0, 240, 255, 0.4);
              transition: all 0.3s ease;
              position: relative;
              display: inline-block;
            }
            
            .param-unit {
              font-size: 10px;
              color: rgba(0, 240, 255, 1);
              font-weight: 600;
              text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
            }
          }
        }
      }
    }
  }
  
  // 无数据状态
  .no-data-state {
    flex: 1 0 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 48px 24px;
    min-height: 200px;
    
    .hologram-container {
      position: relative;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .hologram-ring {
        position: absolute;
        border: 2px solid rgba(0, 240, 255, 0.4);
        border-radius: 50%;
        
        &.ring-1 {
          width: 120px;
          height: 120px;
          animation: pulse-ring 2s ease-in-out infinite;
        }
        
        &.ring-2 {
          width: 90px;
          height: 90px;
          animation: pulse-ring 2s ease-in-out 0.3s infinite;
        }
        
        &.ring-3 {
          width: 60px;
          height: 60px;
          animation: pulse-ring 2s ease-in-out 0.6s infinite;
        }
      }
      
      .hologram-icon {
        position: relative;
        z-index: 1;
        font-size: 48px;
        color: #00f0ff;
        text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
                     0 0 40px rgba(0, 240, 255, 0.6),
                     0 0 60px rgba(0, 240, 255, 0.4);
        animation: float 3s ease-in-out infinite;
        
        i {
          display: block;
        }
      }
    }
    
    .no-data-text {
      text-align: center;
      
      .main-text {
        font-size: 16px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
        margin-bottom: 8px;
        letter-spacing: 1px;
      }
      
      .sub-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.5);
        animation: pulse-text 2s ease-in-out infinite;
      }
    }
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes scanline {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(500px);
    opacity: 0;
  }
}

@keyframes dataflow {
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}
</style>
